import React, { CSSProperties } from "react";
import { Shell } from "@/shell/Shell";
import { PageHeader } from "@/shell/PageHeader";
import { FiltersBar } from "@/features/orders-analytics/components/FiltersBar";
import { useAvailableClients } from "@/features/orders-analytics/hooks/use-available-clients";
import { useOrdersAnalyticsData } from "@/features/orders-analytics/hooks/use-orders-analytics-data";
import { useCustomerIntelligenceData } from "@/features/orders-analytics/hooks/use-customer-intelligence-data";
import { useOrdersAnalyticsFilters } from "@/features/orders-analytics/hooks/use-orders-analytics-filters";
import { SectionHeader } from "@/shell/components/SectionHeader";
import { KpiCard } from "@/shell/components/KpiCard";
import { CohortTriangle } from "@/shell/components/CohortTriangle";
import { customersViewModel } from "@/features/orders-analytics/pages/customers-view-model";
import { timezone } from "@/features/orders-analytics/utils/timezone";

const concentrationStyle = (tint: "default" | "critical" | "ok"): CSSProperties => {
    if (tint === "critical") return { color: "var(--vb-error-500)" };
    if (tint === "ok") return { color: "var(--vb-success-500)" };
    return {};
};

export function CustomersPage() {
    const { filters, setFilters } = useOrdersAnalyticsFilters();
    const { availableClients, loading, refresh } = useAvailableClients(filters);
    const todayStart = timezone.getTimeZoneDayStartTimestamp(Date.now());
    const { data: dashboardData } = useOrdersAnalyticsData(filters, "client", todayStart, null, null);
    const { customerIntelligence } = useCustomerIntelligenceData(filters);

    const vm = customersViewModel.fromData({
        customers: dashboardData.customers,
        cohorts: dashboardData.cohorts,
        customerIntelligence,
    });

    return (
        <Shell title="Customers & retention" subtitle="Cohorts, value, concentration">
            <PageHeader
                title="Customers & retention"
                lede="Customer base, value per workspace, retention cohorts, concentration."
                right={
                    <FiltersBar
                        filters={filters}
                        availableClients={availableClients}
                        onFiltersChange={setFilters}
                        onRefresh={refresh}
                        loading={loading}
                    />
                }
            />

            <section className="section">
                <SectionHeader
                    title="Customer base"
                    count={vm.customerBase.length}
                    meta="acquisition · activation · churn"
                />
                <div className="grid-5">
                    {vm.customerBase.map((kpi) => (
                        <KpiCard
                            key={kpi.label}
                            icon={kpi.icon}
                            label={kpi.label}
                            value={kpi.value}
                            sub={kpi.sub}
                            mom={kpi.mom}
                            unit={kpi.unit}
                            tint={kpi.tint}
                            deltaPolarity={kpi.deltaPolarity}
                        />
                    ))}
                </div>
            </section>

            <section className="section">
                <SectionHeader
                    title="Customer value"
                    count={vm.customerValue.length}
                    meta="per active workspace"
                />
                <div className="grid-4">
                    {vm.customerValue.map((kpi) => (
                        <KpiCard
                            key={kpi.label}
                            icon={kpi.icon}
                            label={kpi.label}
                            value={kpi.value}
                            sub={kpi.sub}
                            mom={kpi.mom}
                            tint={kpi.tint}
                        />
                    ))}
                </div>
            </section>

            <section className="section">
                <div className="grid-12">
                    <div style={{ gridColumn: "span 8" }}>
                        <SectionHeader
                            title="Cohort retention"
                            meta="% of workspaces still active N months after first order"
                        />
                        {vm.cohortRows.length === 0 ? (
                            <div className="td-muted" style={emptySectionStyle}>
                                Sin datos disponibles — no hay fuente real para esta sección.
                            </div>
                        ) : (
                            <div className="card" style={cohortCardStyle}>
                                <CohortTriangle cohorts={vm.cohortRows} months={vm.monthsTracked} />
                                <div style={cohortFooterStyle}>
                                    <span>
                                        Cohorts size avg:{" "}
                                        <strong style={cohortFooterStrongStyle}>{vm.cohortFooter.avgSize}</strong>
                                    </span>
                                    <span>
                                        Avg M3: <strong style={cohortFooterStrongStyle}>{vm.cohortFooter.avgM3}</strong>
                                    </span>
                                    <span>
                                        Avg M6: <strong style={cohortFooterStrongStyle}>{vm.cohortFooter.avgM6}</strong>
                                    </span>
                                    <span>
                                        Avg M12: <strong style={cohortFooterStrongStyle}>{vm.cohortFooter.avgM12}</strong>
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>

                    <div style={{ gridColumn: "span 4" }}>
                        <SectionHeader title="Concentration" meta="dependency risk · top client" />
                        <div style={concentrationColumnStyle}>
                            {vm.concentration.map((tile) => (
                                <div key={tile.label} className="card tile">
                                    <div className="tile__label">{tile.label}</div>
                                    <div className="tile__value num" style={concentrationStyle(tile.tint)}>
                                        {tile.value}
                                    </div>
                                    <div className="tile__sub">{tile.sub}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>
        </Shell>
    );
}

const emptySectionStyle: CSSProperties = { padding: "16px 4px", fontSize: 13 };

const cohortCardStyle: CSSProperties = { padding: 16 };

const cohortFooterStyle: CSSProperties = {
    marginTop: 12,
    paddingTop: 10,
    borderTop: "1px solid var(--vb-border)",
    display: "flex",
    gap: 24,
    fontSize: 12,
    color: "var(--vb-fg-muted)",
    flexWrap: "wrap",
};

const cohortFooterStrongStyle: CSSProperties = { color: "var(--vb-fg-strong)" };

const concentrationColumnStyle: CSSProperties = {
    display: "flex",
    flexDirection: "column",
    gap: 10,
};
